<template>
    <div class="bg-indigo-600" v-if="gamificationEvents.count > 0">
        <div class="max-w-7xl mx-auto py-3 px-3 sm:px-6 lg:px-8">
            <div class="flex items-center justify-between flex-wrap">
                <div class="w-0 flex-1 flex items-center">
                    <span class="flex p-2 rounded-lg bg-indigo-800">
                        <!-- Heroicon name: outline/speakerphone -->
                        <svg
                            class="h-6 w-6 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                            />
                        </svg>
                    </span>
                    <p class="ml-3 font-medium text-white truncate">
                        <span class="md:hidden">
                            {{
                                $tc('messages.X_events_available', gamificationEvents.count, {
                                    count: gamificationEvents.count,
                                })
                            }}.
                        </span>
                        <span class="hidden md:inline">
                            {{
                                $tc('messages.X_events_available', gamificationEvents.count, {
                                    count: gamificationEvents.count,
                                })
                            }}.
                        </span>
                    </p>
                </div>
                <div class="order-3 mt-2 shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
                    <button
                        @click="showModal = true"
                        class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50"
                    >
                        {{ $t('messages.join_in!') }}
                    </button>
                </div>
                <div class="order-2 shrink-0 sm:order-3 sm:ml-3">&nbsp;</div>
            </div>
        </div>

        <Modal :show="showModal" @close="showModal = false">
            <h2 class="text-lg">
                {{ $tc('messages.battles', 2) }}
            </h2>
            <div class="divide-y-2 divide-gray-100">
                <div
                    class="flex justify-between py-2 gap-2"
                    v-for="(battle, index) in gamificationEvents.battles"
                    :key="index"
                >
                    <div>
                        <div class="flex gap-2">
                            <div>
                                <img :src="battle.enemy_image" alt="" class="h-12 inline-block" />
                            </div>
                            <div>
                                <div class="flex gap-2 items-start">
                                    <span>{{ battle.name }}</span>

                                    <BadgePill v-if="battle.status === 'finished'">
                                        {{ battle.status_display }}
                                    </BadgePill>
                                </div>
                                <div>
                                    <div class="flex space-x-2">
                                        <div class="flex space-x-2" v-if="battle.is_reward_shown">
                                            <div>
                                                <img
                                                    class="h-6 w-6"
                                                    src="/assets/gamification/currency/coin.svg"
                                                    alt="emerald"
                                                />
                                            </div>
                                            <div>
                                                {{ battle.coins_amount }}
                                            </div>
                                        </div>
                                        <div class="w-24">
                                            <ProgressBar :percent="battle.enemy_health_percent_display" color="red">
                                                <div>
                                                    <span v-if="battle.is_enemy_health_shown">
                                                        {{ battle.enemy_health_display }}
                                                        /
                                                    </span>
                                                    {{ battle.enemy_health_total }}
                                                </div>
                                            </ProgressBar>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex justify-between py-2 gap-2 items-center">
                        <InertiaSquareLinkButton v-if="battle.links.play" :href="battle.links.play" action="play" />
                        <div :title="$t('messages.help_defeat_the_enemy')">
                            <InertiaSquareLinkButton
                                :href="battle.links.show"
                                :action="battle.status === 'created' ? 'quiz' : 'show'"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <hr v-if="gamificationEvents.battles.length" class="py-2" />

            <h2 class="text-lg">
                {{ $tc('messages.fetch_quests', 2) }}
            </h2>
            <div class="divide-y-2 divide-gray-100">
                <div
                    class="flex justify-between py-2"
                    v-for="(fetchQuest, questIndex) in gamificationEvents.fetchQuests"
                    :key="questIndex"
                >
                    <div>
                        <div class="flex gap-2">
                            <div>
                                <img :src="fetchQuest.enemy_image" alt="" class="h-12 inline-block" />
                            </div>
                            <div>
                                <div class="flex gap-2 items-start">
                                    <span>{{ fetchQuest.name }}</span>

                                    <BadgePill v-if="fetchQuest.status === 'finished'">
                                        {{ fetchQuest.status_display }}
                                    </BadgePill>
                                </div>
                                <div>
                                    <div class="flex space-x-2">
                                        <div class="flex space-x-2" v-if="fetchQuest.is_reward_shown">
                                            <div>
                                                <img
                                                    class="h-6 w-6"
                                                    src="/assets/gamification/currency/coin.svg"
                                                    alt="emerald"
                                                />
                                            </div>
                                            <div>
                                                {{ fetchQuest.coins_amount }}
                                            </div>
                                        </div>
                                        <div class="w-24">
                                            <ProgressBar
                                                :percent="
                                                    Math.min(
                                                        100,
                                                        Math.round(
                                                            (fetchQuest.fetch_quest_actions_count /
                                                                fetchQuest.quantity_total) *
                                                                100,
                                                        ),
                                                    )
                                                "
                                                class="w-full"
                                                color="indigo"
                                            >
                                                {{ fetchQuest.fetch_quest_actions_count }}
                                                /
                                                {{ fetchQuest.quantity_total }}
                                            </ProgressBar>
                                        </div>
                                    </div>
                                    <div class="">
                                        {{ $t('messages.finished_at') }}:
                                        {{ fetchQuest.finished_at }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div :title="$t('messages.join_in!')">
                        <InertiaSquareLinkButton
                            :href="fetchQuest.links.show"
                            :action="fetchQuest.status === 'created' ? 'quiz' : 'show'"
                        />
                    </div>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import Modal from '@/components/Modal.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';
import BadgePill from '@/components/BadgePill.vue';

export default {
    name: 'GamificationEventsDashboardBanner',
    components: {
        BadgePill,
        Modal,
        InertiaSquareLinkButton,
        ProgressBar,
    },
    props: {
        gamificationEvents: {
            required: false,
            type: Object,
        },
    },
    data() {
        return {
            showModal: false,
        };
    },
};
</script>
